/**
 * Service worker registration. Call once from main.tsx (Wave B).
 * No-op in dev, on unsupported browsers, or on failure.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void (async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none',
        });
        registration.addEventListener('updatefound', () => {
          // Do not force reload; surface quietly for future toast UI.
          console.info('[sw] new version available');
        });
        // Force an update check immediately on every boot. Browsers otherwise
        // only check on top-level navigation + every 24h, which strands users
        // whose tab is older than the most recent deploy.
        registration.update().catch(() => {
          // Best-effort; the browser's normal update path is the fallback.
        });
      } catch (err) {
        console.warn('[sw] registration failed', err);
      }
    })();
  });
}

// Build-asset cache prefixes. Media/api/activities are NOT cleared — those
// hold downloaded lessons and would re-cost the user. `workbox-*` covers older
// deploys that generated a Workbox precache before we returned to public/sw.js.
const BUILD_CACHE_PREFIXES = ['shell-', 'assets-immutable-', 'runtime-', 'workbox-'];

function isBuildCache(name: string) {
  return (
    BUILD_CACHE_PREFIXES.some(prefix => name.startsWith(prefix)) ||
    name.toLowerCase().includes('precache')
  );
}

/**
 * Aggressively clear the service worker + build-asset caches so the next page
 * load picks up fresh /index.html and fresh chunk hashes. Used by the stale
 * dynamic-import recovery path (see ErrorBoundary).
 */
export async function clearStaleBuildState(timeoutMs = 1500): Promise<void> {
  if (typeof window === 'undefined') return;

  const tasks: Promise<unknown>[] = [];

  if ('caches' in window) {
    tasks.push(
      (async () => {
        const names = await caches.keys();
        await Promise.all(
          names
            .filter(isBuildCache)
            .map(name => caches.delete(name))
        );
      })()
    );
  }

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    tasks.push(
      (async () => {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(reg => reg.unregister()));
      })()
    );
  }

  try {
    await Promise.race([
      Promise.all(tasks),
      new Promise(resolve => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {
    // Best-effort; we still want to reload.
  }
}
